// Cliente 360 (Ola 3, chunk 3) — Tarjeta "Ciclo de facturación" de la celda 3:
// día de cobro, período activo y próximo período en un layout horizontal con
// divisores sutiles que se apila en móvil. Reusa el patrón visual de
// MetricaCard; los errores degradan el dato a '—' sin tumbar el dashboard.
import type { ReactNode } from 'react';
import { Grid, Typography, theme } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import type { ClienteResponse, CobroClienteResponse } from '@aid/shared';
import { useT } from '../../../locales';
import { StatusTag } from '../../../components/StatusTag';
import { MetricCard } from '../../../components/MetricCard';
import { formatearPeriodo, parseDateOnly } from '../../../lib/date';
import { periodoActivoFacturacion, proximoPeriodoFacturacion } from '../cliente-financiero';

const { Text } = Typography;

const PERIODO_CORTO = 'DD/MM';

interface ClienteCicloFacturacionCardProps {
  cliente: ClienteResponse;
  cobros: CobroClienteResponse[];
  loading: boolean;
  isError: boolean;
}

interface CicloInfoGroupProps {
  label: string;
  value: ReactNode;
  icon?: ReactNode;
  extra?: ReactNode;
}

function CicloInfoGroup({ label, value, icon, extra }: CicloInfoGroupProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <Text
        type="secondary"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 13,
          fontWeight: 500,
          whiteSpace: 'nowrap',
        }}
      >
        {icon && (
          <span
            aria-hidden
            style={{ display: 'inline-flex', fontSize: 18, lineHeight: 1, flexShrink: 0 }}
          >
            {icon}
          </span>
        )}
        {label}
      </Text>
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          fontSize: 16,
          fontWeight: 600,
          lineHeight: 1.3,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
        {extra}
      </span>
    </div>
  );
}

export function ClienteCicloFacturacionCard({
  cliente,
  cobros,
  loading,
  isError,
}: ClienteCicloFacturacionCardProps) {
  const t = useT();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const esMovil = !screens.lg;

  const dia =
    cliente.diaCobro ??
    (cliente.fechaCobro ? (parseDateOnly(cliente.fechaCobro)?.date() ?? null) : null);
  const periodoActivo = periodoActivoFacturacion(cobros, cliente);
  const proximo = proximoPeriodoFacturacion(cliente);

  const periodoActivoValue = isError
    ? '—'
    : (formatearPeriodo(periodoActivo.fechaInicio, periodoActivo.fechaFin, PERIODO_CORTO) ?? '—');
  const proximoValue =
    proximo.fechaInicio && proximo.fechaFin
      ? (formatearPeriodo(proximo.fechaInicio, proximo.fechaFin, PERIODO_CORTO) ?? '—')
      : '—';

  const grupos = [
    {
      label: t('pages.clientes.kpi.diaCobro'),
      icon: <CalendarOutlined />,
      value: dia === null ? '—' : t('pages.clientes.kpi.diaCobroValor', { dia }),
    },
    {
      label: t('pages.clientes.kpi.periodoActivo'),
      value: periodoActivoValue,
      extra:
        !isError && periodoActivo.sinFacturar ? (
          <StatusTag tone="neutral" style={{ fontSize: 12, fontWeight: 600 }}>
            {t('pages.clientes.kpi.sinFacturar')}
          </StatusTag>
        ) : undefined,
    },
    {
      label: t('pages.clientes.kpi.proximoPeriodo'),
      value: proximoValue,
    },
  ];

  const border = `1px solid ${token.colorSplit}`;

  return (
    <MetricCard label={t('pages.clientes.kpi.cicloFacturacion')} loading={loading}>
      <div
        style={{
          display: 'flex',
          flexDirection: esMovil ? 'column' : 'row',
          gap: esMovil ? 16 : 0,
        }}
      >
        {grupos.map((g, i) => (
          <div
            key={g.label}
            style={
              esMovil
                ? { flex: 1, minWidth: 0 }
                : {
                    flex: 1,
                    minWidth: 0,
                    paddingLeft: i === 0 ? 0 : 24,
                    paddingRight: i === grupos.length - 1 ? 0 : 24,
                    borderRight: i === grupos.length - 1 ? 'none' : border,
                  }
            }
          >
            <CicloInfoGroup label={g.label} icon={g.icon} value={g.value} extra={g.extra} />
          </div>
        ))}
      </div>
    </MetricCard>
  );
}
